import type { IObservableArray } from 'mobx';
import { observable } from 'mobx';

import type { TChannelKey } from '@feathr/blackbox';
import { AggregatedStat, DailyStat } from '@feathr/blackbox';

/**
 * Resolves channel-specific stats from the aggregated stat.
 * When a channel key is provided (e.g., 'tradedesk' or 'meta'), extracts that channel's
 * stats from the `channels` object and wraps them in a new AggregatedStat.
 * When undefined (Overview tab), returns the original combined stats.
 *
 * @param stats - The aggregated stat containing combined and per-channel data
 * @param channelKey - The backend channel key (e.g., 'tradedesk', 'meta'), or undefined for combined
 * @returns An AggregatedStat with channel-specific or combined data
 */
function resolveChannelAggregatedStats(
  stats: AggregatedStat,
  channelKey: TChannelKey | undefined,
): AggregatedStat {
  if (!channelKey) {
    return stats;
  }
  const channelData = stats.get('channels')?.[channelKey] ?? {};
  return new AggregatedStat(channelData);
}

/**
 * Resolves channel-specific daily stats from an array of daily stat models.
 * Overlays the channel-specific data onto each daily stat so that existing
 * consumers (ActivityCard, DailyStatsTableCard) work unchanged.
 *
 * @param models - Array of daily stat models
 * @param channelKey - The backend channel key, or undefined for combined
 * @returns Array of DailyStat models with channel-specific or combined data
 */
function resolveChannelDailyStats(
  models: IObservableArray<DailyStat>,
  channelKey: TChannelKey | undefined,
): IObservableArray<DailyStat> {
  if (!channelKey) {
    return models;
  }
  const resolved = models.map((model) => {
    const channelData = model.get('channels')?.[channelKey];
    if (!channelData) {
      return model;
    }
    return new DailyStat({ ...model.toJS(), ...channelData });
  });
  return observable.array(resolved);
}

export { resolveChannelAggregatedStats, resolveChannelDailyStats };
